import React, { useState, useEffect } from "react";
import { Save, CreditCard, Info, Building2, ArrowRightLeft, CheckCircle2, Landmark } from "lucide-react";
import { Spinner } from "react-bootstrap";
import { AppButton, AppCard, AppCardHeader, AppCardTitle, AppCardContent } from "../ui";
import apiClient from "../../utils/apiClient";

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-25 (Phase 5); BankDetailsSettings.scss deleted
// with it. Thirty-three colour literals went with it, in three groups:
//
//   · `var(--accent-primary, #2563eb)` at five call sites — the selected card's
//     border, the radio ring, the radio fill and both focus states. The fallback
//     is the v1 blue, so the one control that says "this is the mode you picked"
//     was the one thing on the page that could not follow the institute's brand.
//     It is `primary`.
//   · Two icon tiles and three info banners, each a hand-mixed Tailwind-palette
//     triple (#ecfdf5 / #065f46 / #a7f3d0 and friends). That is exactly the
//     bg / fg / border shape §4.4 gives every status family: success, info,
//     warning — soft background, -fg ink, the base colour at 30% for the rule.
//   · #059669 / #dc2626 save messages and the error field's four red literals →
//     success / danger.
//
// Nothing here changes which colour means what; it changes where the colour is
// defined, so dark mode and per-institute branding reach all of it.
const CARD_TITLE =
  "tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-content";

const CARD_DESC = "tw-mb-0 tw-mt-1 tw-text-[0.8125rem] tw-text-content-subtle";

const TYPE_CARD =
  "tw-relative tw-flex tw-cursor-pointer tw-items-start tw-gap-3 tw-rounded tw-border-2 " +
  "tw-border-solid tw-bg-surface-raised tw-p-4 tw-text-left " +
  "tw-transition-[border-color,box-shadow,background] tw-duration-200 max-md:tw-p-3.5";

const TYPE_CARD_ACTIVE = `${TYPE_CARD} tw-border-primary tw-shadow-[0_0_0_3px_hsl(var(--primary)/0.08)]`;
const TYPE_CARD_IDLE =
  `${TYPE_CARD} tw-border-line hover:tw-border-line-soft hover:tw-bg-surface-hover`;

const RADIO =
  "tw-mt-0.5 tw-flex tw-h-5 tw-w-5 tw-shrink-0 tw-items-center tw-justify-center " +
  "tw-rounded-full tw-border-2 tw-border-solid tw-transition-[border-color] tw-duration-150";

const RADIO_ACTIVE = `${RADIO} tw-border-primary-solid tw-bg-primary-solid tw-text-primary-solid-fg`;
const RADIO_IDLE = `${RADIO} tw-border-line tw-text-primary`;

const TILE =
  "tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md";

// > svg rather than a class on the icon: <Info> is one element and the rule is
// about its alignment inside the banner, not about the icon itself.
const BANNER =
  "tw-mb-3 tw-flex tw-items-start tw-gap-2 tw-rounded-sm tw-border tw-border-solid " +
  "tw-px-3.5 tw-py-2.5 tw-text-[0.8125rem] tw-leading-[1.5] " +
  "[&>svg]:tw-mt-0.5 [&>svg]:tw-shrink-0";

const BANNER_TONE = {
  emerald: `${BANNER} tw-border-success/30 tw-bg-success-soft tw-text-success-fg`,
  blue: `${BANNER} tw-border-info/30 tw-bg-info-soft tw-text-info-fg`,
  amber: `${BANNER} tw-border-warning/30 tw-bg-warning-soft tw-text-warning-fg`,
};

const SAVE_ROW =
  "tw-mt-1 tw-flex tw-items-center tw-gap-3 max-md:tw-flex-col max-md:tw-items-stretch";

const FIELD =
  "tw-flex tw-flex-col tw-gap-1.5 " +
  "[&>label]:tw-text-[0.6875rem] [&>label]:tw-font-semibold [&>label]:tw-uppercase " +
  "[&>label]:tw-tracking-[0.04em] [&>label]:tw-text-content-muted";

const INPUT =
  "tw-w-full tw-rounded-sm tw-border tw-border-solid tw-bg-surface tw-px-3 tw-py-2 " +
  "tw-text-[0.8125rem] tw-text-content tw-outline-none " +
  "tw-transition-[border-color,box-shadow] tw-duration-150 " +
  "placeholder:tw-text-content-subtle focus:tw-ring-[3px]";

const INPUT_OK = `${INPUT} tw-border-line focus:tw-border-primary focus:tw-ring-primary/[0.08]`;
const INPUT_ERR =
  `${INPUT} tw-border-danger tw-bg-danger/[0.04] focus:tw-border-danger focus:tw-ring-danger/[0.12]`;

const FIELD_ERROR = "tw-mt-1 tw-block tw-text-xs tw-leading-[1.2] tw-text-danger";

const BankDetailsSettings = () => {
  const [institute, setInstitute] = useState(null);
  const [selectedPaymentType, setSelectedPaymentType] = useState("own_gateway");
  const [bankDetails, setBankDetails] = useState({
    accountHolderName: "",
    accountNumber: "",
    ifscCode: "",
    bankName: "",
    upiId: "",
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/institute/settings");
        const inst = data.institute;
        setInstitute(inst);
        setSelectedPaymentType(inst?.paymentType || "own_gateway");
        if (inst?.bankDetails) {
          setBankDetails((prev) => ({ ...prev, ...inst.bankDetails }));
        }
      } catch {
        // defaults
      }
    })();
  }, []);

  const update = (key) => (e) => {
    const value = key === 'ifscCode' ? e.target.value.toUpperCase() : e.target.value;
    setBankDetails((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validateBankDetails = () => {
    const errors = {};
    const bd = bankDetails;
    const hasBankPair = bd.accountNumber && bd.ifscCode;
    const hasUpi = !!bd.upiId;

    if (!hasBankPair && !hasUpi) {
      errors._form = 'Provide either bank account + IFSC OR a UPI ID.';
      return errors;
    }
    if (bd.ifscCode && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(bd.ifscCode)) {
      errors.ifscCode = 'Invalid IFSC (4 letters + 0 + 6 alphanumeric, e.g. SBIN0001234).';
    }
    if (bd.accountNumber && !/^\d{9,18}$/.test(bd.accountNumber)) {
      errors.accountNumber = 'Account number must be 9-18 digits.';
    }
    if (bd.upiId && !/^[a-zA-Z0-9._-]{2,256}@[a-zA-Z]{2,64}$/.test(bd.upiId)) {
      errors.upiId = 'UPI ID must be in name@handle format.';
    }
    if (bd.accountNumber && !bd.accountHolderName) {
      errors.accountHolderName = 'Account holder name is required.';
    }
    return errors;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMsg(null);
      const payload = { paymentType: selectedPaymentType };
      if (selectedPaymentType === "lms_payment") {
        const errors = validateBankDetails();
        if (Object.keys(errors).length > 0) {
          setFieldErrors(errors);
          setMsg({ type: "error", text: errors._form || 'Please fix the highlighted fields.' });
          setSaving(false);
          return;
        }
        setFieldErrors({});
        payload.bankDetails = bankDetails;
      }

      await apiClient.put("/institute/settings", payload);
      setInstitute((prev) => ({ ...prev, paymentType: selectedPaymentType }));
      setMsg({ type: "success", text: "Payment settings saved!" });
    } catch (err) {
      setMsg({ type: "error", text: err?.response?.data?.message || "Failed to save." });
    } finally {
      setSaving(false);
    }
  };

  if (!institute) return null;

  const paymentType = selectedPaymentType || institute.paymentType || "own_gateway";
  const ownActive = paymentType === "own_gateway";
  const lmsActive = paymentType === "lms_payment";

  const saveMsg = msg && (
    <span
      className={`tw-text-[0.8125rem] tw-font-medium ${
        msg.type === "success" ? "tw-text-success" : "tw-text-danger"
      }`}
    >
      {msg.text}
    </span>
  );

  return (
    <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-5">
      {/* ── Payment Type Selector — radio cards ── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <span className={CARD_TITLE}><ArrowRightLeft size={18} /> Payment Mode</span>
          </AppCardTitle>
          <p className={CARD_DESC}>Choose how fee payments are collected and processed</p>
        </AppCardHeader>
        <AppCardContent>
          <div className="tw-mb-4 tw-grid tw-grid-cols-2 tw-gap-3.5 max-md:tw-grid-cols-1">
            {/* Own Razorpay */}
            <button
              type="button"
              className={ownActive ? TYPE_CARD_ACTIVE : TYPE_CARD_IDLE}
              onClick={() => setSelectedPaymentType("own_gateway")}
            >
              <div className={ownActive ? RADIO_ACTIVE : RADIO_IDLE}>
                {ownActive && <CheckCircle2 size={18} />}
              </div>
              <div className={`${TILE} tw-bg-success-soft tw-text-success`}>
                <CreditCard size={22} />
              </div>
              <div className="tw-min-w-0 tw-flex-1">
                <h4 className="tw-mb-1 tw-mt-0 tw-text-sm tw-font-semibold tw-text-content">Own Razorpay</h4>
                <p className="tw-m-0 tw-text-xs tw-leading-[1.4] tw-text-content-subtle">
                  Use your own Razorpay keys. Payments go directly to your account.
                </p>
              </div>
            </button>

            {/* LMS Payouts */}
            <button
              type="button"
              className={lmsActive ? TYPE_CARD_ACTIVE : TYPE_CARD_IDLE}
              onClick={() => setSelectedPaymentType("lms_payment")}
            >
              <div className={lmsActive ? RADIO_ACTIVE : RADIO_IDLE}>
                {lmsActive && <CheckCircle2 size={18} />}
              </div>
              <div className={`${TILE} tw-bg-info-soft tw-text-info`}>
                <Building2 size={22} />
              </div>
              <div className="tw-min-w-0 tw-flex-1">
                <h4 className="tw-mb-1 tw-mt-0 tw-text-sm tw-font-semibold tw-text-content">LMS Payouts</h4>
                <p className="tw-m-0 tw-text-xs tw-leading-[1.4] tw-text-content-subtle">
                  Platform collects payments. Earnings are settled to your bank after commission.
                </p>
              </div>
            </button>
          </div>

          {/* Info banner */}
          <div className={lmsActive ? BANNER_TONE.blue : BANNER_TONE.emerald}>
            <Info size={15} />
            <span>
              {lmsActive
                ? "Fee payments are collected via the platform gateway. A commission is deducted and the remainder is settled to your bank."
                : "You are using your own Razorpay gateway. All payments are collected directly into your Razorpay account."}
            </span>
          </div>

          {/* Pending payout notice */}
          {institute.paymentType === "lms_payment" && ownActive && (
            <div className={BANNER_TONE.amber}>
              <Info size={15} />
              <span>Switching from LMS Payouts to Own Razorpay is blocked while pending payouts exist.</span>
            </div>
          )}

          <div className={SAVE_ROW}>
            <AppButton onClick={handleSave} disabled={saving} size="sm">
              {saving ? <><Spinner size="sm" className="tw-me-1" /> Saving...</>
                : <><Save size={14} className="tw-me-1" /> Save Payment Mode</>}
            </AppButton>
            {saveMsg}
          </div>
        </AppCardContent>
      </AppCard>

      {/* ── Bank Details — only for LMS Payouts ── */}
      {lmsActive && (
        <AppCard>
          <AppCardHeader>
            <AppCardTitle>
              <span className={CARD_TITLE}><Landmark size={18} /> Bank Details for Settlements</span>
            </AppCardTitle>
            <p className={CARD_DESC}>Provide your bank account so the platform can settle your earnings</p>
          </AppCardHeader>
          <AppCardContent>
            <div className="tw-mb-4 tw-grid tw-grid-cols-2 tw-gap-4 max-md:tw-grid-cols-1">
              <div className={FIELD}>
                <label>Account Holder Name</label>
                <input
                  type="text"
                  className={fieldErrors.accountHolderName ? INPUT_ERR : INPUT_OK}
                  value={bankDetails.accountHolderName}
                  onChange={update("accountHolderName")}
                  placeholder="e.g. John Doe"
                />
                {fieldErrors.accountHolderName && <small className={FIELD_ERROR}>{fieldErrors.accountHolderName}</small>}
              </div>
              <div className={FIELD}>
                <label>Account Number</label>
                <input
                  type="text"
                  className={fieldErrors.accountNumber ? INPUT_ERR : INPUT_OK}
                  value={bankDetails.accountNumber}
                  onChange={update("accountNumber")}
                  placeholder="e.g. 1234567890"
                  inputMode="numeric"
                />
                {fieldErrors.accountNumber && <small className={FIELD_ERROR}>{fieldErrors.accountNumber}</small>}
              </div>
              <div className={FIELD}>
                <label>IFSC Code</label>
                <input
                  type="text"
                  className={fieldErrors.ifscCode ? INPUT_ERR : INPUT_OK}
                  value={bankDetails.ifscCode}
                  onChange={update("ifscCode")}
                  placeholder="e.g. SBIN0001234"
                  maxLength={11}
                />
                {fieldErrors.ifscCode && <small className={FIELD_ERROR}>{fieldErrors.ifscCode}</small>}
              </div>
              <div className={FIELD}>
                <label>Bank Name</label>
                <input
                  type="text"
                  className={INPUT_OK}
                  value={bankDetails.bankName}
                  onChange={update("bankName")}
                  placeholder="e.g. State Bank of India"
                />
              </div>
              <div className={`${FIELD} tw-col-span-full`}>
                <label>
                  UPI ID{" "}
                  <span className="tw-font-normal tw-normal-case tw-tracking-normal tw-text-content-subtle">
                    (optional if bank account provided)
                  </span>
                </label>
                <input
                  type="text"
                  className={fieldErrors.upiId ? INPUT_ERR : INPUT_OK}
                  value={bankDetails.upiId}
                  onChange={update("upiId")}
                  placeholder="e.g. name@upi"
                />
                {fieldErrors.upiId && <small className={FIELD_ERROR}>{fieldErrors.upiId}</small>}
              </div>
            </div>

            <div className={SAVE_ROW}>
              <AppButton onClick={handleSave} disabled={saving} size="sm">
                {saving ? <><Spinner size="sm" className="tw-me-1" /> Saving...</>
                  : <><Save size={14} className="tw-me-1" /> Save Bank Details</>}
              </AppButton>
              {saveMsg}
            </div>
          </AppCardContent>
        </AppCard>
      )}
    </div>
  );
};

export default BankDetailsSettings;
